import React, {memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Container} from '../Container';

import "./index.scss"
import {useDispatch, useSelector} from "react-redux"
import {useKeyboard} from "../../hooks/keyboard"
import {useRPC} from "../../hooks/rpc"
import {UserRole} from "../../types/control"
import {hasAuthority} from "../../utils/role"
import {useMessage} from "../../hooks/message"
import {onClickHandler} from "../../utils/onclick"
import {setSession} from "../../utils/session-storage";
import {setUserAction} from "../../store/user/action";
import {useLocalization} from "../../hooks/localization";
import {useHistory} from "react-router-dom";

type Props = {
    children?: ReactNode;
    isFullscreen?: boolean;
    requiredRole: UserRole;
};

function convertToMinute(second: number, message: string) {
    if (second === 0) {
        return (message)
    }
    const min = Math.floor(second / 60)
    const sec = second % 60
    let newSec = String(sec)
    if (String(sec).length === 1) {
        newSec = `0` + sec
    }
    return min + ":" + newSec
}

export const PasswordContainer = memo(function PasswordContainer({children, requiredRole, isFullscreen}: Props) {
    const {t} = useLocalization();
    const role = useSelector(state => state.ui.role);
    const [rpc] = useRPC();
    const {failure} = useMessage();
    const dispatch = useDispatch();
    const history = useHistory();

    const usernameRef = useRef<HTMLInputElement>();
    const passwordRef = useRef<HTMLInputElement>();
    const securityRef = useRef<HTMLInputElement>();

    const [setActiveInput, renderKeyboard] = useKeyboard();
    const [setActivePassword, renderNumpad] = useKeyboard('numpad', true);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [securityControl, setSecurityControl] = useState("")
    const [securityCode, setSecurityCode] = useState("")
    const [sessionCountdown, setSessionCountdown] = useState(0);

    const onFocusUsername = useCallback(() => {
        setActiveInput([usernameRef, username, setUsername]);
    }, [username, setActiveInput]);

    const onFocusPassword = useCallback(() => {
        setActivePassword([passwordRef, password, setPassword]);
    }, [password, setActivePassword]);

    const onFocusSecurityCode = useCallback(() => {
        setActiveInput([securityRef, securityControl, setSecurityControl])
    }, [securityControl, setActiveInput])

    const onBlur = useCallback(() => {
        setActiveInput(null)
        setActivePassword(null)
    }, [setActiveInput, setActivePassword])


    useEffect(() => {
        rpc.GetSecurityCode()
            .then(r => {
                setSecurityCode(r?.security_code)
                setSessionCountdown(r?.session_countdown)
            })
            .catch(console.log)
    }, [rpc])

    const onClickHandleCopy = useCallback(async () => {
        if ("clipboard" in navigator) {
            return await navigator.clipboard.writeText(securityCode)
        } else {
            return document.execCommand("copy", true, securityCode)
        }
    }, [securityCode])

    const onClick = useCallback(() => {
        rpc.authenticate(username, password, securityControl, securityCode)
            .then(() => {
                setSession(username, password, securityControl, securityCode)
                dispatch(setUserAction(username, password, securityControl, securityCode))
                setUsername("")
                setPassword("")
                setSecurityControl("")
                history.push("/reset-password")
            }).then(() => window.location.reload())
            .catch(failure)
    }, [rpc, username, password, failure, dispatch, securityControl, securityCode, history])

    const countdown = convertToMinute(sessionCountdown, t("please_enter_security_code"))

    const onKeyPress = useCallback(
        e => {
            if (e.key === "Enter") {
                onClick()
            }
        },
        [onClick]
    )

    const renderPassword = useCallback(() => {
        return (
            <>
                <div className="page-wrapper password-area">
                    <div className="login-container">
                        <div className="password-container widget-wrapper">
                            <input
                                maxLength={50}
                                placeholder={t("USERNAME")}
                                ref={usernameRef}
                                onChange={e => setUsername(e.target.value)}
                                className="input-button"
                                onFocus={onFocusUsername}
                                onBlur={onBlur}
                                value={username}
                                onKeyPress={onKeyPress}
                            />
                            <input
                                type="password"
                                maxLength={50}
                                placeholder={t("PASSWORD")}
                                ref={passwordRef}
                                onChange={e => setPassword(e.target.value)}
                                className="input-button"
                                onFocus={onFocusPassword}
                                onBlur={onBlur}
                                value={password}
                                onKeyPress={onKeyPress}
                            />
                            {sessionCountdown === 0 && (
                                <div className="security-code-control">
                                    <div className="security-code-user-select" onClick={onClickHandleCopy}>
                                        {securityCode}
                                    </div>
                                    <input
                                        type="text"
                                        maxLength={12}
                                        placeholder={"Kod"}
                                        className="input-button-security"
                                        onChange={e => setSecurityControl(e.target.value)}
                                        onKeyPress={onKeyPress}
                                        onBlur={onBlur}
                                        onFocus={onFocusSecurityCode}
                                        value={securityControl}
                                        ref={securityRef}
                                    />
                                </div>
                            )}
                            {/*<div className='session-countdown-container'>{countdown}</div>*/}
                            <div className="input-button" {...onClickHandler(onClick)}>
                                {t("LOGIN")}
                            </div>
                        </div>
                    </div>
                    {renderIf(role !== "default" && role !== null)(() => (
                        <span
                            style={{
                                color: "red",
                                background: "#0000001a",
                                padding: 10,
                                maxWidth: 252,
                                textAlign: "center",
                                borderRadius: 3
                            }}
                        >
                            {t("YOU_ARE_NOT_AUTHORIZED_TO_ACCESS_THIS_PAGE")}
                        </span>
                    ))}
                </div>
                {renderKeyboard()}
                {renderNumpad()}
            </>
        )
    }, [
        t,
        onBlur,
        onClick,
        onFocusPassword,
        onFocusUsername,
        onFocusSecurityCode,
        password,
        renderKeyboard,
        renderNumpad,
        role,
        username,
        onKeyPress,
        securityCode,
        securityControl,
        onClickHandleCopy,
        sessionCountdown
    ])

    const isAuthenticated = useMemo(() => hasAuthority(role, requiredRole), [requiredRole, role])

    return (
        <Container className={"no-sidebar" + (isFullscreen && isAuthenticated ? " fullscreen" : "")}>
            {renderIf(isAuthenticated)(() => children, renderPassword)}
        </Container>
    )
})
